import { BufferAttribute } from 'three/src/core/BufferAttribute';
import { BufferGeometry } from 'three/src/core/BufferGeometry';
import { Mesh } from 'three/src/objects/Mesh';
import { buildUnfilledSolid } from './fillSolids';
import { BeachSurface } from './fillSurface';
import { FillAnchor } from './stopeGeometry';
import {
  StopeFillPour, StopeFillStep, StopeFillWalk, walkStopeFill
} from './pour';

type AttributeArray = BufferAttribute['array'];

export interface SerialisedGeometry {
  attributes: Array<{ name: string; array: AttributeArray; itemSize: number; normalized: boolean }>;
  index: AttributeArray | null;
  // A CSG result can hold longer arrays than it draws.
  drawStart: number;
  drawCount: number;
}

export interface StopeFillWalkRequest {
  stope: SerialisedGeometry;
  // Already in fill order.
  pours: StopeFillPour[];
  defaultAnchor: FillAnchor;
}

interface SerialisedStep {
  fillSolid: SerialisedGeometry;
  sliceVolume: number;
  datum: number;
  surface: BeachSurface;
  riseAtDischarge: number;
  cumulativeVolume: number;
  beachAngleDegrees: number;
}

export type StopeFillWalkResponse =
  | {
    ok: true;
    steps: SerialisedStep[];
    unfilledSolid: SerialisedGeometry;
    totalVolume: number;
    cumulativeVolume: number;
    surfaces: BeachSurface[];
    dischargeFloorLevel: number;
  }
  | { ok: false; error: string };

export type StopeFillWalkOutcomeStep<T extends StopeFillPour> = Omit<StopeFillStep<T>, 'fillSolid'> & { fillSolid: Mesh };

export interface StopeFillWalkOutcome<T extends StopeFillPour> {
  walk: StopeFillWalk;
  steps: StopeFillWalkOutcomeStep<T>[];
  unfilledSolid: Mesh;
}

export const serialiseGeometry = (geometry: BufferGeometry): SerialisedGeometry => ({
  attributes: Object.entries(geometry.attributes).map(([name, attribute]) => {
    const { array, itemSize, normalized } = attribute as BufferAttribute;
    return {
      name, array, itemSize, normalized,
    };
  }),
  index: geometry.index?.array ?? null,
  drawStart: geometry.drawRange.start,
  drawCount: geometry.drawRange.count,
});

export const deserialiseGeometry = (data: SerialisedGeometry): BufferGeometry => {
  const geometry = new BufferGeometry();
  data.attributes.forEach(({
    name, array, itemSize, normalized,
  }) => geometry.setAttribute(name, new BufferAttribute(array, itemSize, normalized)));
  if (data.index) geometry.setIndex(new BufferAttribute(data.index, 1));
  geometry.setDrawRange(data.drawStart, data.drawCount);
  return geometry;
};

const geometryBuffers = (data: SerialisedGeometry): ArrayBuffer[] => [
  ...data.attributes.map(({ array }) => array.buffer as ArrayBuffer),
  ...(data.index ? [data.index.buffer as ArrayBuffer] : []),
];

// Deduplicated: listing a buffer twice fails the transfer.
export const transferablesOf = (response: StopeFillWalkResponse): ArrayBuffer[] => (
  response.ok
    ? Array.from(new Set([
      ...response.steps.flatMap((step) => geometryBuffers(step.fillSolid)),
      ...geometryBuffers(response.unfilledSolid),
    ]))
    : []
);

const walkFields = ({
  entityId, pourPositionX, pourPositionY, pourHeight, volume, isRockFill, pasteBeachAngleDegrees,
}: StopeFillPour): StopeFillPour => ({
  entityId, pourPositionX, pourPositionY, pourHeight, volume, isRockFill, pasteBeachAngleDegrees,
});

export const toWalkRequest = (stope: Mesh, orderedPours: StopeFillPour[], defaultAnchor: FillAnchor): StopeFillWalkRequest => ({
  stope: serialiseGeometry(stope.geometry),
  pours: orderedPours.map(walkFields),
  defaultAnchor,
});

export const runStopeFillWalkJob = ({ stope, pours, defaultAnchor }: StopeFillWalkRequest): StopeFillWalkResponse => {
  try {
    const pristine = new Mesh(deserialiseGeometry(stope));
    const steps: SerialisedStep[] = [];
    const walk = walkStopeFill(pristine, pours, defaultAnchor, ({
      fillSolid, sliceVolume, datum, surface, riseAtDischarge, cumulativeVolume, beachAngleDegrees,
    }) => {
      steps.push({
        fillSolid: serialiseGeometry(fillSolid.geometry),
        sliceVolume,
        datum,
        surface,
        riseAtDischarge,
        cumulativeVolume,
        beachAngleDegrees,
      });
    });

    return {
      ok: true,
      steps,
      unfilledSolid: serialiseGeometry(buildUnfilledSolid(pristine, walk.surfaces).geometry),
      totalVolume: walk.totalVolume,
      cumulativeVolume: walk.cumulativeVolume,
      surfaces: walk.surfaces,
      dischargeFloorLevel: walk.dischargeFloorLevel,
    };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : String(error) };
  }
};

export const toWalkOutcome = <T extends StopeFillPour>(
  response: StopeFillWalkResponse,
  stope: Mesh,
  orderedPours: T[],
): StopeFillWalkOutcome<T> => {
  if (!response.ok) throw new Error(response.error);

  // Matches the CSG, which with groups off gives every result the stope's material.
  const toMesh = (data: SerialisedGeometry): Mesh => new Mesh(deserialiseGeometry(data), stope.material);

  return {
    walk: {
      pristineStope: stope,
      totalVolume: response.totalVolume,
      cumulativeVolume: response.cumulativeVolume,
      surfaces: response.surfaces,
      dischargeFloorLevel: response.dischargeFloorLevel,
      pourCount: orderedPours.length,
    },
    steps: response.steps.map((step, index) => ({
      ...step,
      pour: orderedPours[index],
      index,
      fillSolid: toMesh(step.fillSolid),
    })),
    unfilledSolid: toMesh(response.unfilledSolid),
  };
};
